import { useId, type ReactNode, type RefObject } from 'react';

/** Props for {@link Card}. */
export interface CardProps {
  /** Optional heading rendered in the card header. */
  title?: ReactNode;
  /** Optional supporting text under the title. */
  description?: ReactNode;
  /** Optional trailing header slot (actions, badges). */
  headerAddon?: ReactNode;
  /** Optional footer region. */
  footer?: ReactNode;
  /** Card body. */
  children?: ReactNode;
  /** Remove the default body padding (e.g. for edge-to-edge tables). */
  flush?: boolean;
  /** Optional extra classes. */
  className?: string;
  /**
   * Page-tour anchor key, rendered as `data-tour` on the root so a guided tour
   * can highlight this card (see `tours/anchors.ts`).
   */
  tourAnchor?: string;
  /**
   * Optional ref to the title heading. Lets a caller that changes `title` in
   * place (e.g. `Wizard` moving between steps) programmatically move keyboard
   * focus to the new heading and have it read out by assistive tech, without
   * reaching into the card's internal markup. Only applied when `title` is set;
   * when provided, the heading also gets `tabIndex={-1}` so it is focusable via
   * script (never added to the natural Tab order).
   */
  headingRef?: RefObject<HTMLHeadingElement>;
}

/**
 * Surface container grouping related content.
 *
 * When a `title` is supplied the card renders a `<section>` labelled by its
 * heading (`aria-labelledby`), giving assistive tech a navigable landmark. The
 * surface, border and shadow are tuned for both themes (spec 08 §7).
 */
export function Card({
  title,
  description,
  headerAddon,
  footer,
  children,
  flush = false,
  className = '',
  tourAnchor,
  headingRef,
}: CardProps): JSX.Element {
  const headingId = useId();
  const hasHeader = title !== undefined || description !== undefined || headerAddon !== undefined;

  return (
    <section
      aria-labelledby={title !== undefined ? headingId : undefined}
      data-tour={tourAnchor}
      className={
        'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ' +
        'dark:border-navy-border dark:bg-navy-surface ' +
        className
      }
    >
      {hasHeader ? (
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-navy-border">
          <div className="min-w-0">
            {title !== undefined ? (
              <h2
                id={headingId}
                className="text-sm font-semibold text-slate-900 dark:text-slate-100"
                {...(headingRef !== undefined ? { ref: headingRef, tabIndex: -1 } : {})}
              >
                {title}
              </h2>
            ) : null}
            {description !== undefined ? (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>
            ) : null}
          </div>
          {headerAddon !== undefined ? <div className="shrink-0">{headerAddon}</div> : null}
        </div>
      ) : null}
      {children !== undefined ? <div className={flush ? '' : 'p-5'}>{children}</div> : null}
      {footer !== undefined ? (
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-navy-border dark:bg-navy-bg/40">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
